import { useState, useEffect } from "react";
import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Zap, ArrowLeft, Building2, Loader2, Check, Smartphone } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BACKEND_URL, API_URL } from "../config/api";
import { hapticSuccess, hapticError, hapticMedium } from "../utils/haptics";
import { registerDeviceWithBackend, getDeviceToken } from "../utils/pushNotifications";
import { DEFAULT_COUNTRY, DEFAULT_CURRENCY, getCountryByCode } from "../constants/countries";

const isNative = Capacitor.isNativePlatform();

const API = API_URL;
const V2_API = `${API_URL}/v2`;
const PLATFORM_API = `${BACKEND_URL}/api/platform`;

// Storage keys
const REMEMBERED_ORG_KEY = 'bf_remembered_org';
const REMEMBERED_USERNAME_KEY = 'bf_remembered_username';
const KEEP_LOGGED_IN_KEY = 'bf_keep_logged_in';

const LoginV2 = () => {
  // Step 1: Organization identification
  const [step, setStep] = useState(1); // 1 = org slug, 2 = credentials, 3 = 2FA verification
  const [orgSlug, setOrgSlug] = useState("");
  const [organization, setOrganization] = useState(null);
  
  // Step 2: Credentials
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Step 3: 2FA Verification
  const [twoFACode, setTwoFACode] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [twoFAEmail, setTwoFAEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // Remember options
  const [rememberOrg, setRememberOrg] = useState(true);
  const [keepLoggedIn, setKeepLoggedIn] = useState(true);
  
  // Super admin override
  const [showSuperAdmin, setShowSuperAdmin] = useState(false);
  const [superAdmin2FAStep, setSuperAdmin2FAStep] = useState(1); // 1 = password, 2 = 2FA code
  const [maskedEmail, setMaskedEmail] = useState("");
  
  // Secret platform admin access
  const [logoClicks, setLogoClicks] = useState(0);
  
  // PWA Install prompt
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  
  const navigate = useNavigate();

  // Listen for PWA install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallButton(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);
  
  // 2FA Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    
    const timer = setInterval(() => {
      setResendCooldown(prev => prev - 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Handle install button click
  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('🎉 App installed successfully!');
    }
    
    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  // Load remembered organization on mount
  useEffect(() => {
    const remembered = localStorage.getItem(REMEMBERED_ORG_KEY);
    if (remembered) {
      try {
        const orgData = JSON.parse(remembered);
        setOrgSlug(orgData.slug);
        // Auto-validate the remembered org
        validateRememberedOrg(orgData.slug);
      } catch (e) {
        localStorage.removeItem(REMEMBERED_ORG_KEY);
      }
    }
  }, []);

  // Validate remembered org silently
  const validateRememberedOrg = async (slug) => {
    try {
      // Use v2 PostgreSQL endpoint for org lookup
      const response = await axios.get(`${V2_API}/auth/organization/${slug}`);
      if (response.data && (response.data.status === 'active' || response.data.status === 'trial')) {
        setOrganization(response.data);
        setStep(2);
        // Load remembered username when moving to step 2
        const rememberedUsername = localStorage.getItem(REMEMBERED_USERNAME_KEY);
        if (rememberedUsername) {
          setUsername(rememberedUsername);
        }
      }
    } catch (e) {
      // Org no longer valid, clear remembered
      localStorage.removeItem(REMEMBERED_ORG_KEY);
    }
  };

  const handleLogoClick = () => {
    setLogoClicks(prev => prev + 1);
    if (logoClicks + 1 >= 3) {
      toast.success("🔓 Secret admin portal unlocked!");
      setTimeout(() => navigate('/platform-admin'), 500);
      setLogoClicks(0);
    }
  };

  // Step 1: Validate organization slug
  const handleOrgContinue = async (e) => {
    e.preventDefault();
    
    if (!orgSlug.trim()) {
      toast.error("Please enter your organization shortname");
      return;
    }

    setLoading(true);
    try {
      // Use v2 PostgreSQL endpoint for org lookup
      const response = await axios.get(`${V2_API}/auth/organization/${orgSlug}`);
      
      if (response.data) {
        const org = response.data;
        
        if (org.status !== 'active' && org.status !== 'trial') {
          // Use same generic message as "not found" to avoid revealing org exists
          toast.error(`Organization '${orgSlug}' not found. Please check the shortname and try again.`);
          setLoading(false);
          return;
        }
        
        // Remember this organization if checkbox is checked
        if (rememberOrg) {
          localStorage.setItem(REMEMBERED_ORG_KEY, JSON.stringify({
            slug: orgSlug,
            name: org.name
          }));
        }
        
        setOrganization(org);
        setStep(2);
        // Load remembered username when moving to step 2
        const rememberedUsername = localStorage.getItem(REMEMBERED_USERNAME_KEY);
        if (rememberedUsername) {
          setUsername(rememberedUsername);
        }
        toast.success(`Welcome to ${org.name}!`);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        toast.error(`Organization '${orgSlug}' not found. Please check the shortname and try again.`);
      } else {
        toast.error("Failed to validate organization. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── WebAuthn / Passkey (Face ID, Touch ID, Windows Hello) ───────────────
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  useEffect(() => {
    // Check if WebAuthn is available (Safari with Face ID, Chrome with biometrics, etc.)
    if (window.PublicKeyCredential &&
        typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(available => setPasskeySupported(available))
        .catch(() => setPasskeySupported(false));
    }
  }, []);

  // ── Native biometric unlock (iOS Face ID / Touch ID via Capacitor) ────────
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const storedJwt = localStorage.getItem('jwtToken');

  useEffect(() => {
    if (!isNative || !storedJwt) return;
    // Dynamically import so web bundle isn't affected
    import('@aparajita/capacitor-biometric-auth').then(({ BiometricAuth }) => {
      BiometricAuth.checkBiometry().then(result => {
        setBiometricAvailable(result.isAvailable);
      }).catch(() => {});
    }).catch(() => {});
  }, [storedJwt]);

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
      await BiometricAuth.authenticate({
        reason: 'Sign in to BookingFlows',
        cancelTitle: 'Use Password',
        iosBiometryType: 'FaceID',
        allowDeviceCredential: true,
      });
      // Biometric passed — use stored JWT
      hapticSuccess();
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (err) {
      hapticError();
      if (err.code === 'userCancel' || err.message?.includes('cancel')) {
        // User cancelled — show normal login, don't toast
      } else {
        toast.error('Biometric sign-in failed. Use your password.');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    try {
      // Step 1: Get challenge — read body ONCE
      const optRes = await axios.post(`${API}/v2/auth/webauthn/authenticate-options`, {
        email: username || '',
        origin: window.location.origin
      });
      const options = optRes.data;
      if (!options.challenge) throw new Error('Invalid server response');

      // Parse challenge from base64url
      const parseBase64url = str => {
        const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
        const binary = atob(b64.padEnd(b64.length + (4 - b64.length % 4) % 4, '='));
        return Uint8Array.from(binary, c => c.charCodeAt(0));
      };

      const publicKeyOptions = {
        challenge: parseBase64url(options.challenge),
        rpId: options.rpId,
        allowCredentials: (options.allowCredentials || []).map(c => ({
          type: 'public-key',
          id: parseBase64url(c.id),
        })),
        timeout: options.timeout || 60000,
        userVerification: options.userVerification || 'preferred',
      };

      // Step 2: Trigger Face ID / Touch ID
      const credential = await navigator.credentials.get({ publicKey: publicKeyOptions });

      // Step 3: Encode and send to server
      const toBase64url = buf => {
        const bytes = new Uint8Array(buf);
        let str = '';
        bytes.forEach(b => str += String.fromCharCode(b));
        return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      };

      const authResponse = {
        id: credential.id,
        rawId: toBase64url(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: toBase64url(credential.response.authenticatorData),
          clientDataJSON: toBase64url(credential.response.clientDataJSON),
          signature: toBase64url(credential.response.signature),
          userHandle: credential.response.userHandle ? toBase64url(credential.response.userHandle) : null,
        },
        email: username || '',
        origin: window.location.origin,  // public URL for server-side origin verification
      };

      const verifyRes = await axios.post(`${API}/v2/auth/webauthn/authenticate`, authResponse);

      if (verifyRes.data.success) {
        const d = verifyRes.data;
        localStorage.setItem('jwtToken', d.token);
        localStorage.setItem('userType', d.userType || 'admin');
        localStorage.setItem('organizationId', d.organizationId || '');
        localStorage.setItem('organizationSlug', d.organizationSlug || '');
        localStorage.setItem('orgSlug', d.organizationSlug || '');
        localStorage.setItem('organizationName', d.organizationName || '');
        localStorage.setItem('orgCountry', d.organizationCountry || 'USA');
        localStorage.setItem('orgCurrency', 'USD');
        // Identify user in Sentry for all subsequent errors
        try {
          const S = await import('@sentry/react');
          S.setUser({ username: d.organizationSlug, id: d.organizationId });
          S.setTag('org_slug', d.organizationSlug || '');
        } catch (_) {}
        localStorage.setItem('userData', JSON.stringify({
          username: d.username,
          name: d.name,
          organizationName: d.organizationName,
          permissions: d.permissions || []
        }));
        // Store team member specific data
        if (d.teamMemberId) localStorage.setItem('teamMemberId', d.teamMemberId);
        if (d.permissions) localStorage.setItem('permissions', JSON.stringify(d.permissions));
        if (d.tabPermissions) localStorage.setItem('tabPermissions', JSON.stringify(d.tabPermissions));
        toast.success('Welcome back!');
        navigate('/admin');
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Biometric authentication was cancelled');
      } else if (err.name === 'InvalidStateError') {
        toast.error('No passkey found on this device. Register one in Settings → Passkeys first.');
      } else {
        console.error('[Passkey login error]', err.name, err.message);
        toast.error(`Sign-in failed: ${err.message || 'Try again or use password'}`);
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  // Step 2: Authenticate user
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error("Please enter both username and password");
      return;
    }

    setLoading(true);
    try {
      // Try PostgreSQL v2 endpoint first
      let response;
      try {
        response = await axios.post(`${API}/v2/auth/login`, {
          orgSlug: orgSlug,
          username: username,
          password: password
        });
      } catch (v2Error) {
        // Fallback to MongoDB endpoint if v2 fails
        response = await axios.post(`${API}/auth/org-login`, {
          orgSlug: orgSlug,
          username: username,
          password: password,
          keepLoggedIn: keepLoggedIn
        });
      }
      
      if (response.data.success) {
        // Check if 2FA is required
        if (response.data.requires2FA || response.data.requires_2fa) {
          setTempToken(response.data.tempToken || response.data.temp_token);
          
          // Send 2FA code automatically
          try {
            const codeResponse = await axios.post(`${API}/auth/2fa/send-code`, {
              orgSlug: orgSlug,
              username: username,
              tempToken: response.data.tempToken || response.data.temp_token
            });
            setTwoFAEmail(codeResponse.data.email);
            toast.success(`Verification code sent to ${codeResponse.data.email}`);
          } catch (err) {
            toast.error("Failed to send verification code");
          }
          
          setStep(3); // Move to 2FA verification step
          setResendCooldown(60); // 60 second cooldown
          return;
        }
        
        // No 2FA - Store authentication data directly
        localStorage.setItem("jwtToken", response.data.token);
        localStorage.setItem("userType", response.data.userType || response.data.user_type);
        localStorage.setItem("organizationId", response.data.organizationId || response.data.organization_id);
        localStorage.setItem("organizationSlug", response.data.organizationSlug || response.data.organization_slug || orgSlug);
        localStorage.setItem("organizationName", response.data.organizationName || response.data.organization_name);
        const orgCountry = response.data.organizationCountry || response.data.organization_country || DEFAULT_COUNTRY;
        localStorage.setItem("orgCountry", orgCountry);
        localStorage.setItem("orgCurrency", response.data.organizationCurrency || response.data.organization_currency || DEFAULT_CURRENCY);
        localStorage.setItem("orgTimezone", response.data.organizationTimezone || response.data.organization_timezone || (getCountryByCode(orgCountry)?.defaultTimezone || 'America/Chicago'));
        // Identify user in Sentry for all subsequent errors
        try {
          const S = await import('@sentry/react');
          const slug = response.data.organizationSlug || response.data.organization_slug || orgSlug;
          const orgId = response.data.organizationId || response.data.organization_id;
          S.setUser({ username: slug, id: orgId });
          S.setTag('org_slug', slug || '');
        } catch (_) {}
        localStorage.setItem("userData", JSON.stringify({
          username: response.data.username,
          name: response.data.name,
          organizationName: response.data.organizationName || response.data.organization_name,
          permissions: response.data.permissions || []
        }));
        
        // Store keep logged in preference
        if (keepLoggedIn) {
          localStorage.setItem(KEEP_LOGGED_IN_KEY, 'true');
          // Remember the username for next time
          localStorage.setItem(REMEMBERED_USERNAME_KEY, username);
        } else {
          // Clear remembered username if not keeping logged in
          localStorage.removeItem(REMEMBERED_USERNAME_KEY);
        }
        
        // Haptic feedback on successful login
        hapticSuccess();
        
        // Register device for push notifications (native app only)
        const pushToken = getDeviceToken();
        if (pushToken && response.data.organizationId) {
          registerDeviceWithBackend(
            response.data.username,
            response.data.organizationId,
            BACKEND_URL,
            response.data.token
          );
        }
        
        toast.success(`Welcome back, ${response.data.name}!`);
        
        setTimeout(() => {
          if (response.data.userType === "admin") {
            navigate("/admin", { replace: true });
          } else if (response.data.userType === "teamMember") {
            navigate("/admin", { replace: true });
          }
        }, 100);
      }
    } catch (error) {
      // Haptic feedback on error
      hapticError();
      
      if (error.response) {
        toast.error(error.response.data.detail || "Invalid credentials");
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // 2FA Code Verification
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error("Please enter the 6-digit verification code");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, {
        orgSlug: orgSlug,
        username: username,
        code: twoFACode,
        tempToken: tempToken
      });
      
      if (response.data.success) {
        // Store authentication data
        localStorage.setItem("jwtToken", response.data.token);
        localStorage.setItem("userType", response.data.userType);
        localStorage.setItem("organizationId", response.data.organizationId);
        localStorage.setItem("organizationSlug", response.data.organizationSlug);
        localStorage.setItem("organizationName", response.data.organizationName);
        localStorage.setItem("orgCountry", response.data.organizationCountry || 'USA');
        localStorage.setItem("userData", JSON.stringify({
          username: response.data.username,
          name: response.data.name,
          organizationName: response.data.organizationName,
          permissions: response.data.permissions || []
        }));
        
        // Store keep logged in preference
        if (keepLoggedIn) {
          localStorage.setItem(KEEP_LOGGED_IN_KEY, 'true');
          localStorage.setItem(REMEMBERED_USERNAME_KEY, username);
        }
        
        toast.success(`Welcome back, ${response.data.name}!`);
        
        setTimeout(() => {
          navigate("/admin", { replace: true });
        }, 100);
      }
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.detail || "Invalid verification code");
      } else {
        toast.error("Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Resend 2FA code
  const handleResend2FACode = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/send-code`, {
        orgSlug: orgSlug,
        username: username,
        tempToken: tempToken
      });
      
      setTwoFAEmail(response.data.email);
      toast.success(`New code sent to ${response.data.email}`);
      setResendCooldown(60);
      setTwoFACode("");
    } catch (error) {
      toast.error("Failed to resend code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Super Admin Login
  const handleSuperAdminLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error("Please enter both username and password");
      return;
    }

    setLoading(true);
    try {
      // Step 1: Send credentials and request 2FA code
      const response = await axios.post(`${API}/auth/super-admin-send-2fa`, {
        username: username,
        password: password
      });
      
      if (response.data.success) {
        setMaskedEmail(response.data.email || "***@***");
        setSuperAdmin2FAStep(2);
        toast.success("2FA code sent to your email");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid super admin credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleSuperAdmin2FAVerify = async (e) => {
    e.preventDefault();
    
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/super-admin-verify-2fa`, {
        username: username,
        code: twoFACode
      });
      
      if (response.data.success && response.data.user_type === "super_admin") {
        // Store super admin data
        localStorage.setItem("jwtToken", response.data.token);
        localStorage.setItem("userType", "super_admin");
        localStorage.setItem("userData", JSON.stringify({
          username: response.data.username,
          name: response.data.name,
          session_id: response.data.session_id
        }));
        
        toast.success("Welcome, Super Admin!");
        navigate("/super-admin");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Invalid code";
      toast.error(msg);
      if (msg.includes("expired") || msg.includes("request a new")) {
        setSuperAdmin2FAStep(1);
        setTwoFACode("");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/super-admin-send-2fa`, {
        username: username,
        password: password
      });
      
      if (response.data.success) {
        toast.success("New code sent to your email");
        setTwoFACode("");
      }
    } catch (error) {
      toast.error("Failed to resend code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Back to step 1
  const handleBack = () => {
    setStep(1);
    setUsername("");
    setPassword("");
    setOrganization(null);
  };

  // Simplified 2FA Verification Screen
  if (!showSuperAdmin && step === 3) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="w-full max-w-sm text-center">
          {/* Shield Icon */}
          <div className="mb-6">
            <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto border-2 border-blue-400/50">
              <svg className="w-10 h-10 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">Security Verification</h1>
          <p className="text-blue-200 text-sm mb-8">
            Enter the 6-digit PIN sent to<br/>
            <span className="text-white font-medium">{twoFAEmail}</span>
          </p>
          
          {/* 6 Digit Input Boxes */}
          <form onSubmit={handleVerify2FA} className="space-y-6">
            <div className="flex justify-center gap-2">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={twoFACode[index] || ''}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 1) {
                      const newCode = twoFACode.split('');
                      newCode[index] = val;
                      setTwoFACode(newCode.join(''));
                      // Auto-focus next input
                      if (val && index < 5) {
                        const nextInput = document.getElementById(`pin-${index + 1}`);
                        nextInput?.focus();
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    // Handle backspace to go to previous input
                    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
                      const prevInput = document.getElementById(`pin-${index - 1}`);
                      prevInput?.focus();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                    setTwoFACode(paste);
                  }}
                  id={`pin-${index}`}
                  disabled={loading}
                  className="w-12 h-14 text-center text-2xl font-bold bg-white/10 border-2 border-blue-400/50 rounded-lg text-white focus:border-blue-400 focus:bg-white/20 focus:outline-none transition-all"
                  autoFocus={index === 0}
                />
              ))}
            </div>
            
            {/* Verify Button */}
            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold bg-blue-500 hover:bg-blue-600 text-white"
              disabled={loading || twoFACode.length !== 6}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                </span>
              ) : (
                "Verify & Sign In"
              )}
            </Button>
          </form>
          
          {/* Footer Links */}
          <div className="mt-6 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => {
                setStep(2);
                setTwoFACode("");
                setTempToken("");
              }}
              className="text-blue-300 hover:text-white transition-colors"
            >
              ← Back to login
            </button>
            <button
              type="button"
              onClick={handleResend2FACode}
              disabled={resendCooldown > 0 || loading}
              className={`font-medium transition-colors ${
                resendCooldown > 0 
                  ? 'text-blue-400/50 cursor-not-allowed' 
                  : 'text-blue-300 hover:text-white'
              }`}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
            </button>
          </div>
          
          <p className="text-xs text-blue-300/70 mt-4">
            Code expires in 10 minutes
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <Card className="w-full max-w-md shadow-xl relative">
        {/* Back button - top left corner */}
        {!showSuperAdmin && (
          <div className="absolute top-4 left-4 z-10">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (step === 2) {
                  setStep(1);
                } else {
                  navigate('/');
                }
              }}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        )}
        
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {/* Show org logo if available (after org is loaded), otherwise default BookingFlows logo */}
            {organization?.logoUrl ? (
              <img 
                src={organization.logoUrl} 
                alt={organization.name || "Company Logo"} 
                onClick={handleLogoClick}
                className="w-16 h-16 rounded-xl cursor-pointer hover:scale-105 transition-transform object-contain"
                onError={(e) => {
                  // Fallback to default logo if org logo fails to load
                  e.target.src = '/logo-icon.png';
                }}
              />
            ) : (
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                onClick={handleLogoClick}
                className="w-16 h-16 rounded-xl cursor-pointer hover:scale-105 transition-transform object-contain"
              />
            )}
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">
            {organization?.name || (
              <>Booking<span className="text-blue-600">Flows</span></>
            )}
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {showSuperAdmin 
              ? superAdmin2FAStep === 2 ? "Two-Factor Authentication" : "Super Admin Access"
              : step === 1 
              ? "Platform Login" 
              : step === 3
              ? "Security Verification"
              : organization?.name ? "Operator Dashboard" : ""
            }
          </p>
        </CardHeader>
        <CardContent>
          {/* Super Admin Login */}
          {showSuperAdmin && superAdmin2FAStep === 1 && (
            <form onSubmit={handleSuperAdminLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="superadmin-username" className="text-sm font-semibold text-gray-700">
                  Username
                </Label>
                <Input
                  id="superadmin-username"
                  type="text"
                  placeholder="superadmin"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  className="h-12 text-base border-2 border-gray-200 focus:border-blue-500"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="superadmin-password" className="text-sm font-semibold text-gray-700">
                  Password
                </Label>
                <Input
                  id="superadmin-password"
                  type="password"
                  placeholder="Enter super admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className="h-12 text-base border-2 border-gray-200 focus:border-blue-500"
                />
              </div>
              <Button 
                type="submit" 
                style={{ backgroundColor: '#2563EB' }}
                className="w-full h-12 text-base font-semibold hover:opacity-90 text-white"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                  </span>
                ) : (
                  "Continue"
                )}
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                className="w-full text-gray-600 hover:text-gray-900"
                onClick={() => {
                  setShowSuperAdmin(false);
                  setUsername("");
                  setPassword("");
                  setSuperAdmin2FAStep(1);
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Organization Login
              </Button>
            </form>
          )}

          {/* Super Admin 2FA Verification */}
          {showSuperAdmin && superAdmin2FAStep === 2 && (
            <form onSubmit={handleSuperAdmin2FAVerify} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-600">
                  Enter the 6-digit code sent to
                </p>
                <p className="font-medium text-gray-900">{maskedEmail}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="2fa-code" className="text-sm font-semibold text-gray-700">
                  Verification Code
                </Label>
                <Input
                  id="2fa-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  className="h-14 text-2xl text-center tracking-[0.5em] font-mono border-2 border-gray-200 focus:border-blue-500"
                  autoFocus
                />
              </div>
              
              <Button 
                type="submit" 
                style={{ backgroundColor: '#2563EB' }}
                className="w-full h-12 text-base font-semibold hover:opacity-90 text-white"
                disabled={loading || twoFACode.length !== 6}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                  </span>
                ) : (
                  "Verify & Sign In"
                )}
              </Button>
              
              <div className="flex items-center justify-center gap-2 text-sm">
                <span className="text-gray-500">Didn't receive it?</span>
                <button
                  type="button"
                  onClick={handleResend2FA}
                  disabled={loading}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Resend Code
                </button>
              </div>
              
              <Button
                type="button"
                variant="ghost"
                className="w-full text-gray-600 hover:text-gray-900"
                onClick={() => {
                  setSuperAdmin2FAStep(1);
                  setTwoFACode("");
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Password
              </Button>
            </form>
          )}

          {/* Step 1: Organization Slug */}
          {!showSuperAdmin && step === 1 && (
            <form onSubmit={handleOrgContinue} className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-0">
                  <Label htmlFor="orgSlug" className="text-sm font-semibold text-gray-700">
                    Organization shortname
                  </Label>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-shortname')}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Don't remember?
                  </button>
                </div>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="orgSlug"
                    type="text"
                    placeholder="e.g., assetx"
                    value={orgSlug}
                    onChange={(e) => setOrgSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    className="pl-10 h-11 text-base border-2 border-gray-200 focus:border-blue-500"
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  This is your company's unique ID (e.g. <span className="font-medium text-gray-500">boatx</span> or <span className="font-medium text-gray-500">bat-city</span>), not your email address.
                </p>
              </div>
              
              {/* Remember me - tiny custom checkbox */}
              <div className="flex items-center gap-1.5">
                <div
                  onClick={() => setRememberOrg(!rememberOrg)}
                  className={`w-[10px] h-[10px] min-w-[10px] min-h-[10px] max-h-[10px] rounded-[2px] border cursor-pointer flex-shrink-0 flex items-center justify-center ${
                    rememberOrg 
                      ? 'bg-blue-600 border-blue-600' 
                      : 'bg-white border-gray-300'
                  }`}
                >
                  {rememberOrg && (
                    <Check className="w-[6px] h-[6px] text-white" strokeWidth={4} />
                  )}
                </div>
                <span 
                  onClick={() => setRememberOrg(!rememberOrg)}
                  className="text-[10px] text-gray-400 cursor-pointer select-none"
                >
                  Remember me
                </span>
              </div>
              
              <Button 
                type="submit" 
                style={{ backgroundColor: '#2563EB' }}
                className="w-full h-11 text-base font-medium hover:opacity-90 text-white"
                disabled={loading || !orgSlug.trim()}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Validating...
                  </span>
                ) : (
                  "Continue →"
                )}
              </Button>
            </form>
          )}

          {/* Step 2: Credentials */}
          {!showSuperAdmin && step === 2 && (
            <form onSubmit={handleLogin} className="space-y-3">

              {/* Native biometric unlock — only shows on iOS/Android if session exists */}
              {isNative && biometricAvailable && (
                <>
                  <Button
                    type="button"
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading}
                    data-testid="biometric-login-btn"
                    className="w-full h-14 bg-gray-950 hover:bg-gray-800 text-white font-semibold text-base rounded-2xl flex items-center justify-center gap-3"
                  >
                    {biometricLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75a.75.75 0 000 1.5h9a.75.75 0 000-1.5h-9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9a3 3 0 100 6 3 3 0 000-6z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5A5.25 5.25 0 0012 18.75a5.25 5.25 0 005.25-11.25" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                        </svg>
                        Continue with Face ID
                      </>
                    )}
                  </Button>
                  <div className="relative my-1">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-2 text-gray-400">or sign in with password</span>
                    </div>
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="username" className="text-sm font-semibold text-gray-700">
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  className="h-11 text-base border-2 border-gray-200 focus:border-blue-500"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className="h-11 text-base border-2 border-gray-200 focus:border-blue-500"
                />
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>
              
              {/* Keep me logged in - tiny custom checkbox */}
              <div className="flex items-center gap-1.5">
                <div
                  onClick={() => setKeepLoggedIn(!keepLoggedIn)}
                  className={`w-[10px] h-[10px] min-w-[10px] min-h-[10px] max-h-[10px] rounded-[2px] border cursor-pointer flex-shrink-0 flex items-center justify-center ${keepLoggedIn ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'}`}
                >
                  {keepLoggedIn && (
                    <Check className="w-[6px] h-[6px] text-white" strokeWidth={4} />
                  )}
                </div>
                <span 
                  onClick={() => setKeepLoggedIn(!keepLoggedIn)}
                  className="text-[10px] text-gray-400 cursor-pointer select-none"
                >
                  Keep me logged in
                </span>
              </div>
              
              <Button 
                type="submit" 
                style={{ backgroundColor: '#2563EB' }}
                className="w-full h-11 text-base font-semibold hover:opacity-90 text-white"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Signing in...
                  </span>
                ) : (
                  "Sign In"
                )}
              </Button>

              {/* Passkey / Face ID — below Sign In, 1px border */}
              {passkeySupported && (
                <div className="relative my-1">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-gray-400">or</span>
                  </div>
                </div>
              )}
              {passkeySupported && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePasskeyLogin}
                  disabled={passkeyLoading}
                  className="w-full h-11 border border-gray-200 hover:border-blue-400 hover:bg-blue-50 gap-2 font-medium text-gray-600"
                  data-testid="passkey-login-btn"
                >
                  {passkeyLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.864 4.243A7.5 7.5 0 0119.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 004.5 10.5a7.464 7.464 0 01-1.15 3.993m1.989 3.559A11.209 11.209 0 008.25 10.5a3.75 3.75 0 117.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 01-3.6 9.75m6.633-4.596a18.666 18.666 0 01-2.485 5.33" />
                      </svg>
                      Sign in with Face ID / Touch ID
                    </span>
                  )}
                </Button>
              )}
            </form>
          )}
          
          {/* Step 3: 2FA Verification */}
          {!showSuperAdmin && step === 3 && (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Enter the 6-digit code sent to<br/>
                  <span className="font-medium text-gray-800">{twoFAEmail}</span>
                </p>
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="twoFACode" className="text-sm font-semibold text-gray-700">
                  Verification Code
                </Label>
                <Input
                  id="twoFACode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  className="h-14 text-2xl text-center tracking-[0.5em] font-mono border-2 border-gray-200 focus:border-blue-500"
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setStep(2);
                    setTwoFACode("");
                    setTempToken("");
                  }}
                  className="text-gray-600 hover:text-gray-800"
                >
                  ← Back to login
                </button>
                <button
                  type="button"
                  onClick={handleResend2FACode}
                  disabled={resendCooldown > 0 || loading}
                  className={`font-medium ${
                    resendCooldown > 0 
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-blue-600 hover:text-blue-800'
                  }`}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
              
              <Button 
                type="submit" 
                style={{ backgroundColor: '#2563EB' }}
                className="w-full h-11 text-base font-semibold hover:opacity-90 text-white"
                disabled={loading || twoFACode.length !== 6}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                  </span>
                ) : (
                  "Verify & Sign In"
                )}
              </Button>
              
              <p className="text-xs text-gray-500 text-center">
                Code expires in 10 minutes. Check your spam folder if you don't see the email.
              </p>
            </form>
          )}
        </CardContent>
        
        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          {/* Install App Button */}
          {showInstallButton && (
            <button
              onClick={handleInstallClick}
              className="mb-3 flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg text-blue-700 hover:from-blue-100 hover:to-indigo-100 transition-all"
            >
              <Smartphone className="h-4 w-4" />
              <span className="text-sm font-medium">Install App for Quick Access</span>
            </button>
          )}
          
          <p className="text-xs text-gray-500">
            Don't have an account?{" "}
            <a 
              href="/signup" 
              className="font-semibold hover:underline"
              style={{color: '#007AFF'}}
            >
              Sign up here
            </a>
          </p>
          <p className="text-xs text-gray-500 mt-2">
            <a 
              href="/" 
              className="font-medium hover:underline"
              style={{color: '#007AFF'}}
            >
              Are you a customer? Browse experiences
            </a>
          </p>
          <p className="text-xs text-gray-400 mt-2">
            © {new Date().getFullYear()} {organization?.name || 'BookingFlows'}. All rights reserved.
          </p>
          {organization?.name && (
            <p className="text-xs text-gray-400 mt-1">
              Powered by <a href="https://bookingflows.com" target="_blank" rel="noopener noreferrer" className="hover:underline">BookingFlows</a>
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default LoginV2;
